/**
 * Carson HVAC — Home Page
 * Design: Light, clean, professional — matching client reference image
 * Colours: Light blue-grey background, #3969a9 steel blue, #971b19 crimson red, #0a1e3d dark navy
 * Section order: Hero+Stats → Our Services → Service Areas → Brands → About Us → Contact → Footer
 */

import { useEffect, useState } from "react";
import {
  Shield,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Star,
  Menu,
  X,
  Award,
  Clock,
  Users,
  Wrench,
} from "lucide-react";
import Seo from "@/components/Seo";

// ─── Custom SVG Service Icons (matching reference design) ─────
const IconAC = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="24" y1="4" x2="24" y2="44" stroke={color} strokeWidth="3" strokeLinecap="round"/>
    <line x1="4" y1="24" x2="44" y2="24" stroke={color} strokeWidth="3" strokeLinecap="round"/>
    <line x1="9.37" y1="9.37" x2="38.63" y2="38.63" stroke={color} strokeWidth="3" strokeLinecap="round"/>
    <line x1="38.63" y1="9.37" x2="9.37" y2="38.63" stroke={color} strokeWidth="3" strokeLinecap="round"/>
    <line x1="24" y1="4" x2="18" y2="10" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="24" y1="4" x2="30" y2="10" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="24" y1="44" x2="18" y2="38" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="24" y1="44" x2="30" y2="38" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="4" y1="24" x2="10" y2="18" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="4" y1="24" x2="10" y2="30" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="44" y1="24" x2="38" y2="18" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="44" y1="24" x2="38" y2="30" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="24" cy="24" r="4" fill={color}/>
  </svg>
);

const IconHeatWaves = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 384 511.4" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill={color} d="M77.46 228.43C65.33 119.85 128.78 43.48 247.72 0c-72.85 94.5 62.09 196.88 69.53 295.03 17.44-29.75 27.34-69.48 29.3-122.55 89.18 139.92 15.25 368.59-181.02 335.73-18.02-3.01-35.38-8.7-51.21-17.17C42.76 452.8 0 369.53 0 290c0-50.69 21.68-95.95 49.74-131.91 3.75 35.23 11.73 61.51 27.72 70.34z"/>
    <path fill={color} fillOpacity="0.45" d="M139.16 372.49c-21.83-57.66-18.81-150.75 42.33-183.41.43 107.03 103.57 120.64 84.44 234.9 17.64-20.39 26.51-53.02 28.1-78.75 27.96 65.38 6.04 117.72-33.81 144.37-121.15 81-225.48-83.23-156.11-173.26 2.08 20.07 26.14 51.12 35.05 56.15z"/>
  </svg>
);

const IconFurnace = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="8" width="36" height="32" rx="3" stroke={color} strokeWidth="2.5" fill="none"/>
    <rect x="12" y="14" width="10" height="14" rx="2" stroke={color} strokeWidth="2" fill="none"/>
    <rect x="26" y="14" width="10" height="14" rx="2" stroke={color} strokeWidth="2" fill="none"/>
    <line x1="12" y1="34" x2="36" y2="34" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <circle cx="17" cy="21" r="2.5" fill={color}/>
    <circle cx="31" cy="21" r="2.5" fill={color}/>
    <line x1="6" y1="4" x2="6" y2="8" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="42" y1="4" x2="42" y2="8" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const IconVentilation = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="2.5" fill="none"/>
    <path d="M24 22 C22 18 18 16 14 18 C16 22 20 22 24 22Z" fill={color}/>
    <path d="M26 24 C30 22 32 18 30 14 C26 16 26 20 26 24Z" fill={color}/>
    <path d="M24 26 C26 30 30 32 34 30 C32 26 28 26 24 26Z" fill={color}/>
    <path d="M22 24 C18 26 16 30 18 34 C22 32 22 28 22 24Z" fill={color}/>
    <circle cx="24" cy="24" r="4" fill={color}/>
    <circle cx="24" cy="24" r="2" fill="white"/>
  </svg>
);

const IconMiniSplit = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="10" width="40" height="16" rx="4" stroke={color} strokeWidth="2.5" fill="none"/>
    <rect x="8" y="14" width="8" height="8" rx="1.5" fill={color} opacity="0.3"/>
    <line x1="20" y1="15" x2="38" y2="15" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="20" y1="19" x2="38" y2="19" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="20" y1="23" x2="32" y2="23" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M10 30 Q14 34 10 38" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M18 30 Q22 34 18 38" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M26 30 Q30 34 26 38" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/>
  </svg>
);

const IconBoiler = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="10" y="6" width="28" height="34" rx="6" stroke={color} strokeWidth="2.5" fill="none"/>
    <circle cx="24" cy="16" r="5" stroke={color} strokeWidth="2" fill="none"/>
    <line x1="24" y1="13" x2="24" y2="16" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="24" y1="16" x2="27" y2="16" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="24" y1="40" x2="24" y2="44" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="18" y1="44" x2="30" y2="44" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M18 26 C17 24 19 22 18 20" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M24 26 C23 24 25 22 24 20" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M30 26 C29 24 31 22 30 20" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/>
  </svg>
);

const IconGasFitting = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="20" width="40" height="8" rx="4" stroke={color} strokeWidth="2.5" fill="none"/>
    <rect x="20" y="4" width="8" height="16" rx="3" stroke={color} strokeWidth="2.5" fill="none"/>
    <circle cx="24" cy="10" r="5" stroke={color} strokeWidth="2.5" fill="none"/>
    <line x1="24" y1="5" x2="24" y2="15" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="19" y1="10" x2="29" y2="10" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="4" y1="20" x2="4" y2="28" stroke={color} strokeWidth="3" strokeLinecap="round"/>
    <line x1="44" y1="20" x2="44" y2="28" stroke={color} strokeWidth="3" strokeLinecap="round"/>
    <rect x="20" y="28" width="8" height="14" rx="3" stroke={color} strokeWidth="2.5" fill="none"/>
  </svg>
);

const IconDuctWork = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="16" width="40" height="10" rx="2" stroke={color} strokeWidth="2.5" fill="none"/>
    <rect x="10" y="26" width="6" height="14" rx="1.5" stroke={color} strokeWidth="2" fill="none"/>
    <rect x="21" y="26" width="6" height="14" rx="1.5" stroke={color} strokeWidth="2" fill="none"/>
    <rect x="32" y="26" width="6" height="14" rx="1.5" stroke={color} strokeWidth="2" fill="none"/>
    <rect x="20" y="4" width="8" height="12" rx="1.5" stroke={color} strokeWidth="2" fill="none"/>
    <line x1="24" y1="4" x2="24" y2="7" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const IconCustomDesign = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="6" width="32" height="38" rx="3" stroke={color} strokeWidth="2.5" fill="none"/>
    <rect x="16" y="2" width="16" height="8" rx="2" stroke={color} strokeWidth="2" fill="none"/>
    <line x1="14" y1="20" x2="34" y2="20" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="14" y1="26" x2="34" y2="26" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="14" y1="32" x2="26" y2="32" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M30 30 L36 36 L32 40 L26 34 Z" stroke={color} strokeWidth="2" fill="none" strokeLinejoin="round"/>
    <line x1="36" y1="36" x2="40" y2="40" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const IconFireplace = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 42 L4 12 L10 6 L38 6 L44 12 L44 42 Z" stroke={color} strokeWidth="2.5" fill="none" strokeLinejoin="round"/>
    <path d="M14 42 L14 28 Q14 18 24 18 Q34 18 34 28 L34 42" stroke={color} strokeWidth="2" fill="none"/>
    <path d="M24 38 C20 34 20 30 24 26 C26 30 28 28 26 24 C30 28 30 34 24 38Z" fill={color} opacity="0.8"/>
  </svg>
);

const IconWaterHeater = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="12" y="8" width="24" height="28" rx="5" stroke={color} strokeWidth="2.5" fill="none"/>
    <path d="M20 18 C19 16 21 14 20 12" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M24 18 C23 16 25 14 24 12" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M28 18 C27 16 29 14 28 12" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/>
    <line x1="18" y1="36" x2="18" y2="44" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="30" y1="36" x2="30" y2="44" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="14" y1="44" x2="34" y2="44" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M24 4 L24 8" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="20" y1="4" x2="28" y2="4" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const IconGenerator = ({ color, size = 38 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="14" width="40" height="22" rx="3" stroke={color} strokeWidth="2.5" fill="none"/>
    <circle cx="16" cy="25" r="6" stroke={color} strokeWidth="2" fill="none"/>
    <circle cx="16" cy="25" r="2.5" fill={color}/>
    <path d="M30 18 L26 25 L30 25 L26 32" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <line x1="36" y1="14" x2="36" y2="10" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="32" y1="10" x2="40" y2="10" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="12" cy="38" r="3" stroke={color} strokeWidth="2" fill="none"/>
    <circle cx="36" cy="38" r="3" stroke={color} strokeWidth="2" fill="none"/>
  </svg>
);

// ─── Image URLs ───────────────────────────────────────────────
const ABOUT_TEAM =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663484265687/7EXZhz75mgWTpwFAMVeVNP/about-team-kdNLH5v3XZvBBKhxmnnwi2.webp";
const HERO_BG_DESKTOP = "/optimized/hero-desktop.webp";
const HERO_BG_MOBILE = "/optimized/hero-mobile.webp";
const SECTION_BG = "/optimized/bottom-bg.webp";
const LOGO_URL = "/optimized/carson-logo.webp";
const BUILT_BY_BILL_URL = "/optimized/builtbybill.webp";
const BUILT_BY_BILL_ICON_URL = "/optimized/builtbybill-favicon.webp";
const FORMSPREE_ENDPOINT = "https://formspree.io/f/xlgawwln";

// ─── Colours ──────────────────────────────────────────────────
// Primary brand colors
const BLUE = "#3969a9";
const BLUE_LIGHT = "#5585c5";
const RED = "#971b19";
const RED_LIGHT = "#c42b2b";
const NAVY = "#0a1e3d";
const NAVY_MID = "#2a4a82";

// Background & surface colors
const BG = "#edf2f7";
const BORDER = "#dce8f5";

// Text colors (dark to light)
const TEXT_BODY = "#4a5568";        // Body text (7.5:1 contrast)
const TEXT_MUTED = "#6b7280";       // Secondary/muted text (4.8:1 contrast)
const TEXT_SUBTLE = "#6a6f73";      // Subtle labels (5.08:1 on white, 4.51:1 on BG - WCAG AA)

// ─── Services Data ────────────────────────────────────────────
const services = [
  { SvgIcon: IconAC, name: "Air Conditioning", desc: "Expert AC installation, repair & maintenance for year-round comfort.", hot: false },
  { SvgIcon: IconHeatWaves, name: "Heating", desc: "Reliable heating solutions to keep your home warm through BC winters.", hot: true },
  { SvgIcon: IconMiniSplit, name: "Ductless Mini-Splits", desc: "Flexible, energy-efficient heating and cooling without ductwork.", hot: false },
  { SvgIcon: IconFurnace, name: "Furnaces", desc: "High-efficiency furnace installation and professional servicing.", hot: true },
  { SvgIcon: IconVentilation, name: "Ventilation", desc: "Proper ventilation systems for healthy indoor air quality.", hot: false },
  { SvgIcon: IconBoiler, name: "Boilers", desc: "Efficient boiler installation, service, and replacement.", hot: true },
  { SvgIcon: IconCustomDesign, name: "Custom Design", desc: "Tailored HVAC system design for unique residential and commercial needs.", hot: false },
  { SvgIcon: IconGasFitting, name: "Gas Fitting", desc: "Certified gas fitting services for safe, compliant installations.", hot: true },
  { SvgIcon: IconGenerator, name: "Generators", desc: "Backup generator installation to keep you powered during outages.", hot: false },
  { SvgIcon: IconFireplace, name: "Fireplaces", desc: "Gas fireplace installation, repair, and maintenance services.", hot: true },
  { SvgIcon: IconDuctWork, name: "Custom Duct Work", desc: "Custom-designed duct systems for optimal airflow and efficiency.", hot: false },
  { SvgIcon: IconWaterHeater, name: "On-Demand Water Heaters", desc: "Tankless water heater installation for endless hot water efficiency.", hot: true },
];

const brandLogos = [
  { name: "American Standard", src: "/brands/americanstandard.png" },
  { name: "Daikin", src: "/brands/daikin.png" },
  { name: "Napoleon", src: "/brands/napoleon.png" },
  { name: "Navien", src: "/brands/navien.png", imageClassName: "max-h-[72px] md:max-h-[78px]" },
  { name: "Panasonic", src: "/brands/panasonic.png", imageClassName: "max-h-[72px] md:max-h-[78px]" },
  { name: "Rheem", src: "/brands/rheem.png" },
  { name: "Rinnai", src: "/brands/rinnai.png" },
  { name: "Tosot", src: "/brands/tosat.png" },
];

const quickLinks = [
  { label: "Home", id: "hero" },
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Contact", id: "contact" },
];

const values = [
  { icon: Shield, title: "Trusted", desc: "A trusted name in the industry, known for unwavering commitment to excellence and community." },
  { icon: Award, title: "Fair Prices", desc: "Dedicated to cost-effective solutions — we prioritize your budget without compromising quality." },
  { icon: Clock, title: "Fast & Responsive", desc: "A proven track record of delivering top-notch mechanical solutions on time, every time." },
  { icon: CheckCircle2, title: "Satisfaction Guaranteed", desc: "When you choose Carson HVAC, you're choosing a company you can rely on completely." },
];

const contactHighlights = [
  {
    title: "Family-Owned Service",
    desc: "Straightforward communication, dependable workmanship, and responsive follow-through from a local team.",
  },
  {
    title: "Free Estimates",
    desc: "Tell us about your project and we will help you understand the right next step without pressure.",
  },
  {
    title: "Residential & Commercial",
    desc: "Support for homeowners, builders, property managers, and businesses across lower Vancouver Island.",
  },
];

const COMPANY_PHONE = "250-744-9725";
const COMPANY_PHONE_LINK = "tel:+12507449725";
const COMPANY_EMAIL = "ken@carsonhvacgroup.com";
const COMPANY_EMAIL_LINK = `mailto:${COMPANY_EMAIL}`;
const COMPANY_ADDRESS = "113-2785 Leigh Rd, Victoria, BC";

const faqs = [
  {
    question: "How long has Carson HVAC been serving Vancouver Island?",
    answer:
      "Carson HVAC is a family-owned company with more than 20 years of experience serving homeowners and businesses across lower Vancouver Island.",
  },
  {
    question: "What areas does Carson HVAC serve?",
    answer:
      "We serve lower Vancouver Island, including Victoria, Langford, Colwood, Saanich, Esquimalt, View Royal, Oak Bay, Metchosin, Highlands, Central Saanich, North Saanich, and Sidney.",
  },
  {
    question: "What HVAC services do you offer?",
    answer:
      "We install, repair, and maintain air conditioning, heating systems, furnaces, boilers, ductless mini-splits, ventilation systems, custom duct work, gas appliances, fireplaces, generators, and on-demand water heaters.",
  },
  {
    question: "Do you offer free estimates?",
    answer:
      "Yes. Call us or send a message through the estimate form and we will arrange the right follow-up for your project.",
  },
  {
    question: "Do you work with residential and commercial customers?",
    answer:
      "Yes. Carson HVAC supports both residential and commercial HVAC projects across the region.",
  },
];

// ─── Scroll Animation Hook ────────────────────────────────────
function useScrollAnimation() {
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (prefersReducedMotion) {
      document
        .querySelectorAll(".fade-in-up, .fade-in-left, .fade-in-right")
        .forEach((el) => el.classList.add("visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -30px 0px" }
    );
    document.querySelectorAll(".fade-in-up, .fade-in-left, .fade-in-right").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [prefersReducedMotion]);
}

// ─── Section Header ───────────────────────────────────────────
function SectionHeader({
  title,
  accentWord,
  light = false,
}: {
  title: string;
  accentWord?: string;
  light?: boolean;
}) {
  const text = accentWord && title.endsWith(` ${accentWord}`)
    ? title.slice(0, -(` ${accentWord}`).length)
    : title;

  return (
    <div className="text-center mb-10 fade-in-up">
      <h2
        style={{
          fontFamily: "'Anton', sans-serif",
          fontSize: "clamp(1.7rem, 5vw, 2.4rem)",
          color: light ? "white" : NAVY,
          letterSpacing: "1px",
          fontWeight: 400,
          marginBottom: "0.6rem",
          lineHeight: 1.15,
        }}
      >
        {text}
        {accentWord ? (
          <>
            {" "}
            <span style={{ color: light ? "white" : BLUE }}>{accentWord}</span>
          </>
        ) : null}
      </h2>
      <div className="mx-auto rounded-full" style={{ width: 100, height: 3, background: light ? "rgba(255,255,255,0.7)" : BLUE }} />
    </div>
  );
}

// ─── Navbar ───────────────────────────────────────────────────
function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!mobileOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen]);

  return (
    <header
      className="site-header fixed top-0 left-0 right-0 z-50"
      style={{
        background: "white",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        height: 60,
      }}
    >
      <div className="container h-full">
        <div className="flex items-center justify-between h-full">
          {/* Logo — overhangs below the navbar */}
          <button
            type="button"
            onClick={() => scrollTo("hero")}
            className="site-logo-shell flex-shrink-0 relative flex items-center justify-center"
            aria-label="Scroll to top"
            style={{
              zIndex: 60,
              width: 96,
              height: 96,
              marginTop: 38,
              borderRadius: "50%",
              background: "white",
              boxShadow: "0 3px 16px rgba(0,0,0,0.14)",
              border: "3px solid #edf2f7",
            }}
          >
            <img
              src={LOGO_URL}
              alt="Carson HVAC - Victoria BC Heating and Cooling Company Logo"
              fetchPriority="high"
              style={{ height: 78, width: 78, objectFit: "contain" }}
            />
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {quickLinks.map(({ label, id }) => (
              <button
                key={id}
                type="button"
                onClick={() => scrollTo(id)}
                className="font-semibold text-sm transition-colors duration-200 relative group"
                style={{ fontFamily: "Open Sans, sans-serif", color: NAVY }}
              >
                {label}
                <span
                  className="absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full rounded-full"
                  style={{ background: BLUE }}
                />
              </button>
            ))}
          </nav>

          {/* Phone (desktop/tablet) + hamburger */}
          <div className="flex items-center gap-3">
            <a
              href={COMPANY_PHONE_LINK}
              className="hidden md:flex items-center gap-2 font-bold text-sm"
              style={{ color: RED, fontFamily: "Open Sans, sans-serif" }}
            >
              <Phone size={15} />
              {COMPANY_PHONE}
            </a>
            {/* Mobile phone — always visible on small screens */}
            <a
              href={COMPANY_PHONE_LINK}
              className="site-mobile-phone md:hidden flex items-center gap-1.5 font-bold text-xs"
              aria-label={`Call Carson HVAC at ${COMPANY_PHONE}`}
              style={{ color: RED, fontFamily: "Open Sans, sans-serif" }}
            >
              <Phone size={14} />
              <span className="site-mobile-phone-label">{COMPANY_PHONE}</span>
            </a>
            <button
              type="button"
              className="md:hidden p-2.5 rounded-lg"
              style={{ color: NAVY }}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <nav
          className="absolute top-[60px] right-0 md:hidden transition-all duration-300 ease-out"
          aria-label="Mobile navigation"
          style={{
            background: "white",
            boxShadow: "0 8px 32px rgba(10,30,61,0.18)",
            zIndex: 55,
            minWidth: 220,
            borderRadius: "0 0 0 16px",
            borderTop: `3px solid ${BLUE}`,
            overflow: "hidden",
          }}
        >
          <div className="flex flex-col px-5 py-3 text-center">
            {quickLinks.map(({ label, id }, index) => (
              <button
                key={id}
                type="button"
                onClick={() => scrollTo(id)}
                className="group relative text-center font-semibold py-4 transition-all duration-200"
                style={{
                  fontFamily: "Open Sans, sans-serif",
                  color: NAVY,
                  borderBottom: index < 3 ? "1px solid rgba(57,105,169,0.12)" : "none",
                  fontSize: "1.05rem",
                  letterSpacing: "0.02em",
                  opacity: 1,
                  transform: "translateX(0)",
                  transitionDelay: `${index * 80}ms`,
                }}
              >
                <span className="relative inline-block">
                  {label}
                  <span
                    className="absolute left-0 bottom-0 h-[2px] w-0 group-hover:w-full transition-all duration-300 ease-out"
                    style={{ background: BLUE }}
                  />
                </span>
              </button>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

// ─── Hero Section ─────────────────────────────────────────────
function HeroSection() {
  return (
    <section
      id="hero"
      className="hero-section relative flex flex-col items-center text-center"
      style={{
        paddingTop: "clamp(110px, 26vw, 150px)",
        paddingBottom: "clamp(92px, 14vw, 132px)",
        marginBottom: "-44px",
      }}
    >
      <picture className="absolute inset-0">
        <source media="(max-width: 768px)" srcSet={HERO_BG_MOBILE} />
        <img
          src={HERO_BG_DESKTOP}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          loading="eager"
          width={1536}
          height={1024}
          className="h-full w-full object-cover object-top"
        />
      </picture>
      <div className="hero-content relative z-10 w-full" style={{ maxWidth: 680, margin: "0 auto", paddingLeft: 20, paddingRight: 20 }}>
        {/* Headline */}
        <h1
          className="leading-tight mb-4"
          style={{
            fontFamily: "'Anton', sans-serif",
            fontSize: "clamp(2rem, 7vw, 3.6rem)",
            color: NAVY,
            lineHeight: 1.12,
            letterSpacing: "1px",
            fontWeight: 400,
          }}
        >
          Your Locally Owned<br />HVAC Solutions
        </h1>

        {/* Subtext */}
        <p
          className="mb-8 mx-auto"
          style={{
            fontFamily: "Open Sans, sans-serif",
            fontSize: "clamp(0.9rem, 3.5vw, 1rem)",
            color: TEXT_BODY,
            lineHeight: 1.65,
            fontWeight: 400,
            maxWidth: 380,
          }}
        >
          Family-owned. Serving Lower Vancouver Island. Installation, repair &amp; maintenance done right.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-10">
          <button
            type="button"
            className="font-bold text-white uppercase tracking-wider transition-all hover:opacity-90 w-full sm:w-auto"
            style={{
              background: RED,
              fontFamily: "Open Sans, sans-serif",
              letterSpacing: "0.07em",
              fontSize: "clamp(0.8rem, 3.5vw, 0.875rem)",
              padding: "14px 32px",
              borderRadius: "8px",
              boxShadow: "0 3px 12px rgba(151,27,25,0.30)",
            }}
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
          >
            Get a Free Estimate
          </button>
          <a
            href={COMPANY_PHONE_LINK}
            className="flex items-center justify-center gap-2 font-bold transition-all hover:shadow-md w-full sm:w-auto"
            style={{
              border: `1.5px solid ${BORDER}`,
              color: NAVY,
              background: "white",
              fontFamily: "Open Sans, sans-serif",
              fontSize: "clamp(0.8rem, 3.5vw, 0.875rem)",
              padding: "14px 28px",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Phone size={15} style={{ color: BLUE }} />
            {COMPANY_PHONE}
          </a>
        </div>

        <div className="stats-section grid grid-cols-3 gap-3 sm:gap-6 md:gap-10 max-w-3xl mx-auto w-full">
          {[
            { icon: Clock, number: "20+", label: "Years of Service" },
            { icon: Users, number: "1000+", label: "Happy Clients" },
            { icon: Wrench, number: "12+", label: "HVAC Services" },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col items-center fade-in-up">
              <div
                className="relative flex flex-col items-center justify-center"
                style={{
                  width: "clamp(100px, 28vw, 200px)",
                  height: "clamp(100px, 28vw, 200px)",
                }}
              >
                <svg
                  className="absolute inset-0 w-full h-full"
                  viewBox="0 0 200 200"
                  fill="none"
                  style={{ pointerEvents: "none" }}
                >
                  <circle cx="100" cy="100" r="90" fill="white" />
                  <path d="M 4 100 A 96 96 0 0 1 196 100" stroke={BLUE} strokeWidth="2" strokeLinecap="round" fill="none" />
                  <path d="M 10 100 A 90 90 0 0 1 190 100" stroke={BLUE} strokeWidth="1.5" strokeLinecap="round" fill="none" />
                  <path d="M 4 100 A 96 96 0 0 0 196 100" stroke={RED} strokeWidth="2" strokeLinecap="round" fill="none" />
                  <path d="M 10 100 A 90 90 0 0 0 190 100" stroke={RED} strokeWidth="1.5" strokeLinecap="round" fill="none" />
                </svg>
                <div className="relative flex flex-col items-center justify-center z-10">
                  <stat.icon
                    size={28}
                    style={{ color: NAVY, marginBottom: 4, strokeWidth: 2 }}
                  />
                  <div
                    style={{
                      fontFamily: "'Anton', sans-serif",
                      fontSize: "clamp(1.3rem, 5vw, 2.4rem)",
                      color: NAVY,
                      lineHeight: 1,
                      letterSpacing: "0.5px",
                    }}
                  >
                    {stat.number}
                  </div>
                  <div
                    className="text-center"
                    style={{
                      fontFamily: "Open Sans, sans-serif",
                      fontSize: "clamp(0.65rem, 2.2vw, 0.8rem)",
                      color: TEXT_BODY,
                      marginTop: 3,
                      fontWeight: 600,
                      lineHeight: 1.2,
                      paddingLeft: 6,
                      paddingRight: 6,
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Bottom hero-to-page transition band */}
      <div
        className="pointer-events-none absolute left-0 right-0 bottom-0"
        style={{ height: "clamp(118px, 17vw, 168px)" }}
      >
        <div
          className="absolute inset-x-0 bottom-0 z-0"
          style={{
            height: "clamp(74px, 11vw, 104px)",
            background: "transparent",
          }}
        />

        <svg
          viewBox="0 0 1440 168"
          preserveAspectRatio="none"
          className="absolute inset-0 z-10 h-full w-full"
          aria-hidden="true"
          style={{ transform: "translateY(clamp(44px, 6vw, 60px))" }}
        >
          <defs>
            <radialGradient id="hero-divider-glint" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
              <stop offset="55%" stopColor="rgba(255,255,255,0.55)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </radialGradient>
          </defs>
          <path
            d="M0,96 C260,108 520,108 800,98 C1060,88 1250,82 1440,80"
            fill="none"
            stroke="rgba(255,255,255,0.72)"
            strokeWidth="26"
            strokeLinecap="round"
            transform="translate(0 10)"
          />
          <path
            d="M0,96 C260,108 520,108 800,98 C1060,88 1250,82 1440,80"
            fill="none"
            stroke="#3969a9"
            strokeWidth="6"
            strokeLinecap="round"
          />
          <ellipse cx="368" cy="106" rx="38" ry="7" fill="url(#hero-divider-glint)" />
          <path
            d="M0,96 C260,108 520,108 800,98 C1060,88 1250,82 1440,80"
            fill="none"
            stroke="#8fb1de"
            strokeWidth="11"
            strokeLinecap="round"
            transform="translate(0 18)"
          />
          <ellipse cx="372" cy="124" rx="42" ry="9" fill="url(#hero-divider-glint)" opacity="0.85" />
        </svg>
      </div>
    </section>
  );
}

// ─── Services Section ─────────────────────────────────────────
function ServicesSection() {
  const [hoveredService, setHoveredService] = useState<string | null>(null);

  return (
    <section
      id="services"
      className="relative overflow-hidden"
      style={{
        marginTop: "-20px",
        paddingTop: "clamp(52px, 10vw, 96px)",
        paddingBottom: "4rem",
      }}
    >
      <div className="container relative z-10">
        <SectionHeader title="Our Services" />
        <p
          className="text-center mx-auto mb-8"
          style={{
            fontFamily: "Open Sans, sans-serif",
            color: TEXT_BODY,
            fontSize: "clamp(0.875rem, 3.5vw, 1rem)",
            maxWidth: 620,
            lineHeight: 1.65,
          }}
        >
          We offer a complete range of HVAC services for homeowners and businesses across lower Vancouver Island. Explore our services or{" "}
          <a href="#contact" style={{ color: BLUE, fontWeight: 700 }}>
            contact our team
          </a>{" "}
          for a free estimate.
        </p>
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          {services.map((service, i) => {
            const accent = service.hot ? RED : BLUE;
            return (
              <div
                key={service.name}
                className="fade-in-up group flex flex-col items-center text-center rounded-2xl transition-all duration-300 hover:shadow-lg"
                onMouseEnter={() => setHoveredService(service.name)}
                onMouseLeave={() => setHoveredService(null)}
                style={{
                  background:
                    hoveredService === service.name
                      ? `linear-gradient(180deg, ${accent} 0%, ${accent}DD 18%, rgba(255,255,255,0.96) 100%)`
                      : "white",
                  border: `1px solid ${BORDER}`,
                  boxShadow: "0 4px 18px rgba(57,105,169,0.10)",
                  minHeight: "clamp(116px, 19vw, 188px)",
                  padding: "clamp(16px, 4vw, 28px) clamp(12px, 3vw, 20px) clamp(14px, 3.5vw, 24px)",
                  transitionDelay: `${(i % 3) * 60}ms`,
                }}
              >
                <div className="transition-all duration-300 group-hover:[filter:brightness(0)_invert(1)]" style={{ marginBottom: 12 }}>
                  <service.SvgIcon color={accent} size={40} />
                </div>
                <h3
                  className="transition-colors duration-300"
                  style={{
                    fontFamily: "'Anton', sans-serif",
                    color: hoveredService === service.name ? "white" : NAVY,
                    fontSize: "clamp(0.72rem, 2.2vw, 1rem)",
                    letterSpacing: "0.5px",
                    fontWeight: 400,
                    lineHeight: 1.2,
                  }}
                >
                  {service.name}
                </h3>
                <p
                  className="hidden transition-colors duration-300 sm:block"
                  style={{
                    fontFamily: "Open Sans, sans-serif",
                    color: hoveredService === service.name ? NAVY : TEXT_MUTED,
                    fontSize: "clamp(0.75rem, 2.5vw, 0.875rem)",
                    lineHeight: 1.5,
                  }}
                >
                  {service.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Service Areas Section ────────────────────────────────────
function ServiceAreasSection() {
  const areas = [
    "Victoria", "Langford", "Colwood", "Saanich", "Esquimalt",
    "View Royal", "Oak Bay", "Metchosin", "Highlands", "Central Saanich",
    "North Saanich", "Sidney",
  ];
  return (
    <section className="py-16">
      <div className="container">
        <SectionHeader title="Service Areas"/>
        <p
          className="text-center mx-auto mb-8"
          style={{
            fontFamily: "Open Sans, sans-serif",
            color: TEXT_BODY,
            fontSize: "clamp(0.875rem, 3.5vw, 1rem)",
            maxWidth: 640,
            lineHeight: 1.6,
          }}
        >
          Serving the lower Vancouver Island, our team is ready to help homeowners and businesses across lower Vancouver Island. Learn more{" "}
          <a href="#about" style={{ color: BLUE, fontWeight: 700 }}>
            about Carson HVAC
          </a>{" "}
          or{" "}
          <a href="#contact" style={{ color: BLUE, fontWeight: 700 }}>
            contact us
          </a>.
        </p>
        <div className="flex flex-wrap justify-center gap-2.5 fade-in-up">
          {areas.map((area) => (
            <div
              key={area}
              className="flex items-center gap-1.5 rounded-full font-semibold transition-all hover:shadow-md"
              style={{
                background: BG,
                border: `1px solid ${BORDER}`,
                color: NAVY,
                fontFamily: "Open Sans, sans-serif",
                fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                padding: "8px 13px",
              }}
            >
              <MapPin size={13} style={{ color: RED, flexShrink: 0 }} />
              {area}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Brands Section ───────────────────────────────────────────
function BrandsSection() {
  const [visibleCount, setVisibleCount] = useState(3);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth < 768) {
        setVisibleCount(2);
        return;
      }

      setVisibleCount(4);
    };

    updateVisibleCount();
    window.addEventListener("resize", updateVisibleCount);

    return () => window.removeEventListener("resize", updateVisibleCount);
  }, []);

  useEffect(() => {
    const maxIndex = Math.max(brandLogos.length - visibleCount, 0);
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [visibleCount]);

  useEffect(() => {
    const maxIndex = Math.max(brandLogos.length - visibleCount, 0);

    if (maxIndex === 0) return;

    const interval = window.setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 2600);

    return () => window.clearInterval(interval);
  }, [visibleCount]);

  return (
    <section className="py-14 relative overflow-hidden">
      <div className="container relative z-10">
        <SectionHeader title="Brands We Carry" />
        <p
          className="text-center mx-auto mb-7"
          style={{
            fontFamily: "Open Sans, sans-serif",
            color: TEXT_BODY,
            fontSize: "clamp(0.875rem, 3.5vw, 1rem)",
            maxWidth: 620,
            lineHeight: 1.65,
          }}
        >
          We work with trusted HVAC and mechanical brands selected for long-term reliability, efficiency, and support.
        </p>
        <div className="fade-in-up max-w-5xl mx-auto">
          <div
            className="relative overflow-hidden rounded-[24px]"
            style={{
              padding: "10px 8px",
            }}
          >
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * (100 / visibleCount)}%)` }}
            >
              {brandLogos.map((brand) => (
                <div
                  key={brand.name}
                  className="shrink-0 px-2"
                  style={{ width: `${100 / visibleCount}%` }}
                >
                  <div
                    className="mx-auto flex h-[84px] w-full max-w-[148px] md:h-[92px] md:max-w-[180px] items-center justify-center transition-all"
                    style={{
                      padding: "12px 14px",
                    }}
                  >
                    <img
                      src={brand.src}
                      alt={`${brand.name} HVAC brand logo`}
                      loading="lazy"
                      className={`${brand.imageClassName ?? "max-h-[62px] md:max-h-[68px]"} w-auto object-contain`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── About Section ────────────────────────────────────────────
function AboutSection() {
  return (
    <section id="about" className="pb-16 pt-8">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Image */}
          <div className="relative fade-in-left hidden lg:block">
            <div className="rounded-2xl overflow-hidden shadow-xl" style={{ aspectRatio: "4/3" }}>
              <img src={ABOUT_TEAM} alt="Carson HVAC professional technicians - family-owned HVAC company serving Victoria and Vancouver Island" loading="lazy" className="w-full h-full object-cover" />
            </div>
            {/* Floating badge — positioned inside image boundary on mobile */}
            <div
              className="absolute flex items-center gap-3 bg-white rounded-xl shadow-lg"
              style={{
                bottom: -16,
                right: 12,
                padding: "10px 16px",
                border: `1px solid ${BORDER}`,
              }}
            >
              <div
                className="flex items-center justify-center text-white font-black text-base flex-shrink-0"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${RED}, ${RED_LIGHT})`,
                  fontFamily: "'Anton', sans-serif",
                  letterSpacing: "0.5px",
                }}
              >
                20+
              </div>
              <div>
                <div className="font-bold text-sm" style={{ color: NAVY, fontFamily: "'Anton', sans-serif", letterSpacing: "0.5px" }}>Years</div>
                <div className="text-xs text-gray-500" style={{ fontFamily: "Open Sans, sans-serif" }}>of Service</div>
              </div>
            </div>

            <div
              className="mt-8 rounded-2xl hidden lg:block"
              style={{
                background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`,
                padding: "clamp(20px, 5vw, 32px)",
              }}
            >
              <div
                className="font-bold uppercase mb-2"
                style={{
                  color: "rgba(255,255,255,0.55)",
                  fontFamily: "Open Sans, sans-serif",
                  fontSize: "0.7rem",
                  letterSpacing: "0.12em",
                }}
              >
                Our Mission &amp; Values
              </div>
              <h3
                style={{
                  fontFamily: "'Anton', sans-serif",
                  color: "white",
                  fontSize: "clamp(1.1rem, 4vw, 1.3rem)",
                  letterSpacing: "0.5px",
                  fontWeight: 400,
                  marginBottom: 10,
                }}
              >
                Exceptional service, every time
              </h3>
              <p
                className="leading-relaxed"
                style={{
                  color: "rgba(255,255,255,0.78)",
                  fontFamily: "Open Sans, sans-serif",
                  fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                }}
              >
                We believe every home and business deserves reliable, efficient HVAC solutions. Our commitment to quality workmanship, honest pricing, and lasting relationships is what sets Carson HVAC apart.
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="fade-in-right mt-6 lg:mt-0 text-center md:text-left">
            <div className="mb-5">
              <h2
                className="leading-tight"
                style={{
                  fontFamily: "'Anton', sans-serif",
                  fontSize: "clamp(1.7rem, 5vw, 2.4rem)",
                  color: NAVY,
                  letterSpacing: "1px",
                  fontWeight: 400,
                  marginBottom: "0.6rem",
                }}
              >
                About Us
              </h2>
              <div
                className="mx-auto md:mx-0 rounded-full"
                style={{ width: 100, height: 3, background: BLUE }}
              />
            </div>
            <p
              className="leading-relaxed mb-4"
              style={{
                fontFamily: "Open Sans, sans-serif",
                color: TEXT_BODY,
                fontSize: "clamp(0.875rem, 3.5vw, 1rem)",
              }}
            >
              Carson HVAC is a family-owned and operated business that has been dedicated to serving the lower Vancouver Island community for over 20+ years.
            </p>
            <p
              className="leading-relaxed mb-8"
              style={{
                fontFamily: "Open Sans, sans-serif",
                color: TEXT_BODY,
                fontSize: "clamp(0.875rem, 3.5vw, 1rem)",
              }}
            >
              Our team of highly skilled professionals are dedicated to providing top-notch service and innovative solutions to ensure optimal comfort and energy efficiency in your home or business.
            </p>

            <div className="mt-5 rounded-2xl overflow-hidden shadow-xl lg:hidden" style={{ aspectRatio: "4/3" }}>
              <img
                src={ABOUT_TEAM}
                alt="Carson HVAC professional technicians - family-owned HVAC company serving Victoria and Vancouver Island"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="mt-5 rounded-2xl lg:hidden"
              style={{
                background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`,
                padding: "clamp(20px, 5vw, 32px)",
              }}
            >
              <div
                className="font-bold uppercase mb-2"
                style={{
                  color: "rgba(255,255,255,0.55)",
                  fontFamily: "Open Sans, sans-serif",
                  fontSize: "0.7rem",
                  letterSpacing: "0.12em",
                }}
              >
                Our Mission &amp; Values
              </div>
              <h3
                style={{
                  fontFamily: "'Anton', sans-serif",
                  color: "white",
                  fontSize: "clamp(1.1rem, 4vw, 1.3rem)",
                  letterSpacing: "0.5px",
                  fontWeight: 400,
                  marginBottom: 10,
                }}
              >
                Exceptional service, every time
              </h3>
              <p
                className="leading-relaxed"
                style={{
                  color: "rgba(255,255,255,0.78)",
                  fontFamily: "Open Sans, sans-serif",
                  fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                }}
              >
                We believe every home and business deserves reliable, efficient HVAC solutions. Our commitment to quality workmanship, honest pricing, and lasting relationships is what sets Carson HVAC apart.
              </p>
            </div>

            {/* Values grid — single column on mobile, 2 cols on sm+ */}
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-3 lg:mt-0">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="flex items-start gap-3 rounded-xl text-left"
                  style={{ background: BG, border: `1px solid ${BORDER}`, padding: "14px 16px" }}
                >
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 8,
                      background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
                      color: "white",
                    }}
                  >
                    <v.icon size={21} />
                  </div>
                  <div>
                    <div
                      className="font-bold mb-0.5"
                      style={{
                        fontFamily: "'Anton', sans-serif",
                        color: NAVY,
                        fontSize: "clamp(0.75rem, 2.5vw, 0.88rem)",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {v.title}
                    </div>
                    <div
                      className="leading-relaxed"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        color: TEXT_MUTED,
                        fontSize: "clamp(0.7rem, 2vw, 0.8rem)",
                      }}
                    >
                      {v.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Contact Section ──────────────────────────────────────────
function ContactSection() {
  const [formData, setFormData] = useState({ name: "", company: "", phone: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    setIsSubmitting(true);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          company: formData.company.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          message: formData.message.trim(),
        }),
      });

      if (!response.ok) {
        const responseData = await response.json().catch(() => null) as
          | { errors?: Array<{ message?: string }> }
          | null;
        const errorMessage = responseData?.errors?.[0]?.message;
        throw new Error(errorMessage || "Something went wrong. Please try again or call us directly.");
      }

      setSubmitted(true);
      setFormData({ name: "", company: "", phone: "", email: "", message: "" });
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Something went wrong. Please try again or call us directly.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "13px 16px",
    borderRadius: 10,
    border: `1.5px solid ${BORDER}`,
    fontFamily: "Open Sans, sans-serif",
    fontSize: "1rem",
    color: NAVY,
    background: "white",
    outline: "none",
    transition: "border-color 0.2s",
    WebkitAppearance: "none",
  };

  return (
    <section
      id="contact"
      className="py-16 relative overflow-hidden"
    >
      <div className="container relative z-10">
        <SectionHeader title="Contact Us" />

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start max-w-5xl mx-auto">
          {/* Contact info */}
          <div className="fade-in-left">
            {/* Contact cards — 3 across, icon + label always visible */}
            <div className="contact-cards-grid grid grid-cols-3 gap-2 sm:gap-3 mb-7">
              <a
                href={COMPANY_PHONE_LINK}
                aria-label={`Call Carson HVAC at ${COMPANY_PHONE}`}
                className="flex flex-col items-center gap-2 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center bg-white"
                style={{ border: `1px solid ${BORDER}`, padding: "14px 8px" }}
              >
                <div
                  className="flex items-center justify-center text-white"
                  style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})` }}
                >
                  <Phone size={18} />
                </div>
                <div>
                  <div
                    className="uppercase tracking-wide"
                    style={{ fontFamily: "Open Sans, sans-serif", color: TEXT_SUBTLE, fontSize: "0.6rem" }}
                  >
                    Call Us
                  </div>
                  <div
                    className="font-bold"
                    style={{ fontFamily: "Open Sans, sans-serif", color: NAVY, fontSize: "clamp(0.65rem, 2.5vw, 0.8rem)" }}
                  >
                    {COMPANY_PHONE}
                  </div>
                </div>
              </a>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(COMPANY_ADDRESS)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open Carson HVAC location in Google Maps"
                className="flex flex-col items-center gap-2 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center bg-white"
                style={{ border: `1px solid ${BORDER}`, padding: "14px 8px" }}
              >
                <div
                  className="flex items-center justify-center text-white"
                  style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})` }}
                >
                  <MapPin size={18} />
                </div>
                <div>
                  <div
                    className="uppercase tracking-wide"
                    style={{ fontFamily: "Open Sans, sans-serif", color: TEXT_SUBTLE, fontSize: "0.6rem" }}
                  >
                    Location
                  </div>
                  <div
                    className="font-bold"
                    style={{ fontFamily: "Open Sans, sans-serif", color: NAVY, fontSize: "clamp(0.65rem, 2.5vw, 0.8rem)" }}
                  >
                    Victoria, BC
                  </div>
                </div>
              </a>
              <a
                href={COMPANY_EMAIL_LINK}
                aria-label={`Email Carson HVAC at ${COMPANY_EMAIL}`}
                className="flex flex-col items-center gap-2 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center bg-white"
                style={{ border: `1px solid ${BORDER}`, padding: "14px 8px" }}
              >
                <div
                  className="flex items-center justify-center text-white"
                  style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})` }}
                >
                  <Mail size={18} />
                </div>
                <div>
                  <div
                    className="uppercase tracking-wide"
                    style={{ fontFamily: "Open Sans, sans-serif", color: TEXT_SUBTLE, fontSize: "0.6rem" }}
                  >
                    Email
                  </div>
                  <div
                    className="font-bold"
                    style={{ fontFamily: "Open Sans, sans-serif", color: NAVY, fontSize: "clamp(0.65rem, 2.5vw, 0.8rem)" }}
                  >
                    Email Us
                  </div>
                </div>
              </a>
            </div>

            {/* Trust highlights */}
            <div className="space-y-3">
              {contactHighlights.map((item) => (
                <div key={item.title} className="rounded-xl bg-white text-left" style={{ border: `1px solid ${BORDER}`, padding: "16px 18px" }}>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" style={{ color: RED }} />
                    <div className="flex-1 text-left justify-start">
                      <div
                        className="font-bold text-left"
                        style={{ fontFamily: "'Anton', sans-serif", color: NAVY, fontSize: "0.95rem", letterSpacing: "0.4px" }}
                      >
                        {item.title}
                      </div>
                      <p
                        className="leading-relaxed text-left"
                        style={{
                          fontFamily: "Open Sans, sans-serif",
                          color: TEXT_BODY,
                          fontSize: "clamp(0.8rem, 3vw, 0.9rem)",
                        }}
                      >
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="fade-in-right">
            {submitted ? (
              <div
                className="text-center rounded-2xl bg-white"
                style={{ border: `1px solid ${BORDER}`, padding: "48px 24px" }}
              >
                <CheckCircle2 size={48} style={{ color: BLUE, margin: "0 auto 16px" }} />
                <h3
                  style={{
                    fontFamily: "'Anton', sans-serif",
                    color: NAVY,
                    fontSize: "1.4rem",
                    letterSpacing: "0.5px",
                    marginBottom: 8,
                  }}
                >
                  Message Sent!
                </h3>
                <p style={{ fontFamily: "Open Sans, sans-serif", color: TEXT_BODY }}>We'll be in touch shortly.</p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-4 rounded-2xl bg-white"
                style={{ border: `1px solid ${BORDER}`, padding: "clamp(20px, 5vw, 32px)" }}
              >
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  autoComplete="organization"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="sr-only"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="block font-bold mb-1.5 uppercase tracking-wide"
                      style={{ fontFamily: "Open Sans, sans-serif", color: TEXT_MUTED, fontSize: "0.7rem" }}
                    >
                      Name *
                    </label>
                    <input
                      id="contact-name"
                      style={inputStyle}
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      autoComplete="name"
                      required
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-phone"
                      className="block font-bold mb-1.5 uppercase tracking-wide"
                      style={{ fontFamily: "Open Sans, sans-serif", color: TEXT_MUTED, fontSize: "0.7rem" }}
                    >
                      Phone
                    </label>
                    <input
                      id="contact-phone"
                      style={inputStyle}
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your phone"
                      type="tel"
                      autoComplete="tel"
                      inputMode="tel"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="block font-bold mb-1.5 uppercase tracking-wide"
                    style={{ fontFamily: "Open Sans, sans-serif", color: TEXT_MUTED, fontSize: "0.7rem" }}
                  >
                    Email *
                  </label>
                  <input
                    id="contact-email"
                    style={inputStyle}
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-message"
                    className="block font-bold mb-1.5 uppercase tracking-wide"
                    style={{ fontFamily: "Open Sans, sans-serif", color: TEXT_MUTED, fontSize: "0.7rem" }}
                  >
                    Message *
                  </label>
                  <textarea
                    id="contact-message"
                    style={{ ...inputStyle, minHeight: 120, resize: "vertical" }}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                    placeholder="Tell us about your HVAC needs..."
                  />
                </div>
                {submitError && (
                  <p role="alert" style={{ color: RED, fontFamily: "Open Sans, sans-serif", fontSize: "0.875rem" }}>
                    {submitError}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full font-bold text-white uppercase tracking-wide transition-all hover:opacity-90 hover:shadow-lg disabled:opacity-60"
                  style={{
                    background: RED,
                    fontFamily: "Open Sans, sans-serif",
                    fontSize: "0.875rem",
                    padding: "15px",
                    borderRadius: 10,
                    letterSpacing: "0.07em",
                  }}
                >
                  {isSubmitting ? "Sending…" : "Send Message"}
                </button>
                <p
                  className="text-center"
                  style={{
                    fontFamily: "Open Sans, sans-serif",
                    color: TEXT_SUBTLE,
                    fontSize: "0.78rem",
                    lineHeight: 1.5,
                  }}
                >
                  Prefer to speak with someone directly? Call{" "}
                  <a href={COMPANY_PHONE_LINK} style={{ color: NAVY, fontWeight: 700 }}>
                    {COMPANY_PHONE}
                  </a>
                  .
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openQuestion, setOpenQuestion] = useState<number | null>(0);

  return (
    <section className="py-16" aria-label="Frequently asked questions">
      <div className="container max-w-4xl">
        <SectionHeader title="Frequently Asked Questions" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {faqs.map((faq, index) => {
            const isOpen = openQuestion === index;

            return (
              <div
                key={faq.question}
                className={`rounded-2xl bg-white fade-in-up ${index === 0 ? "md:col-span-2" : ""}`}
                style={{ border: `1px solid ${BORDER}`, boxShadow: "0 8px 28px rgba(10,30,61,0.06)" }}
              >
                <button
                  type="button"
                  onClick={() => setOpenQuestion(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${index}`}
                  className="w-full text-left flex items-center justify-between gap-4"
                  style={{ padding: "18px 20px" }}
                >
                  <span
                    style={{
                      fontFamily: "'Anton', sans-serif",
                      color: NAVY,
                      fontSize: "clamp(0.95rem, 3vw, 1.1rem)",
                      letterSpacing: "0.4px",
                      lineHeight: 1.2,
                    }}
                  >
                    {faq.question}
                  </span>
                  <span
                    aria-hidden="true"
                    className="flex h-9 w-9 items-center justify-center rounded-full"
                    style={{
                      background: isOpen ? `linear-gradient(135deg, ${RED}, ${RED_LIGHT})` : BG,
                      color: isOpen ? "white" : NAVY,
                      flexShrink: 0,
                    }}
                  >
                    <X
                      size={16}
                      style={{
                        transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </span>
                </button>
                <div
                  id={`faq-panel-${index}`}
                  style={{
                    overflow: "hidden",
                    maxHeight: isOpen ? "180px" : "0",
                    opacity: isOpen ? 1 : 0,
                    transition: "max-height 0.24s ease, opacity 0.18s ease",
                  }}
                >
                  <p
                    style={{
                      padding: "0 20px 20px",
                      fontFamily: "Open Sans, sans-serif",
                      color: TEXT_BODY,
                      fontSize: "clamp(0.875rem, 3vw, 0.95rem)",
                      lineHeight: 1.7,
                    }}
                  >
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────
function Footer() {
  return (
    <footer>
      <div className="container py-1 sm:py-10">
        <div className="flex flex-col items-center gap-3 sm:gap-5">
          <div className="flex flex-col items-center gap-3 sm:hidden">
            <div style={{ width: "100%", display: "flex", justifyContent: "center", overflow: "hidden" }}>
              <div
                className="inline-flex items-center gap-1.5 rounded-full font-semibold"
                style={{
                  background: "rgba(255,255,255,0.70)",
                  border: `1px solid ${BLUE}`,
                  color: NAVY,
                  fontSize: "clamp(0.55rem, 3vw, 0.875rem)",
                  padding: "6px clamp(8px, 2.5vw, 18px)",
                  whiteSpace: "nowrap",
                }}
              >
                <Star size={10} fill={BLUE} style={{ color: BLUE, flexShrink: 0 }} />
                Serving Lower Vancouver Island for Over 20 Years
              </div>
            </div>
            <div
              className="w-full"
              style={{ borderTop: `1px solid rgba(57,105,169,0.25)` }}
            />
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: "50%",
                background: "white",
                boxShadow: "0 3px 16px rgba(0,0,0,0.25)",
                border: "3px solid rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <img src={LOGO_URL} alt="Carson HVAC - Victoria BC Heating and Cooling Company Logo" width={160} height={160} style={{ height: 78, width: 78, objectFit: "contain" }} />
            </div>
          </div>

          <div className="hidden w-full sm:flex sm:flex-col sm:items-center sm:gap-4">
            <div style={{ width: "100%", display: "flex", justifyContent: "center", overflow: "hidden" }}>
              <div
                className="inline-flex items-center gap-1.5 rounded-full font-semibold"
                style={{
                  background: "rgba(255,255,255,0.70)",
                  border: `1px solid ${BLUE}`,
                  color: NAVY,
                  fontSize: "clamp(0.55rem, 3vw, 0.875rem)",
                  padding: "6px clamp(8px, 2.5vw, 18px)",
                  whiteSpace: "nowrap",
                }}
              >
                <Star size={10} fill={BLUE} style={{ color: BLUE, flexShrink: 0 }} />
                Serving Lower Vancouver Island for Over 20 Years
              </div>
            </div>
            <div
              className="w-full"
              style={{ borderTop: `1px solid rgba(57,105,169,0.25)` }}
            />
          </div>

          <div className="w-full text-center border-0">
            <div
              className="hidden sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-6"
            >
              <p
                style={{
                  color: "rgba(10,30,61,0.50)",
                  fontFamily: "Open Sans, sans-serif",
                  fontSize: "0.75rem",
                }}
                className="text-left"
              >
                © {new Date().getFullYear()}. All Rights Reserved.
              </p>
              <div
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  background: "white",
                  boxShadow: "0 3px 16px rgba(0,0,0,0.25)",
                  border: "3px solid rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <img src={LOGO_URL} alt="Carson HVAC - Victoria BC Heating and Cooling Company Logo" width={160} height={160} style={{ height: 78, width: 78, objectFit: "contain" }} />
              </div>
              <a
                href="https://builtbybill.design/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-self-end gap-0 transition-opacity hover:opacity-80"
                aria-label="Built By Bill website"
              >
                <img
                  src={BUILT_BY_BILL_URL}
                  alt="Built By Bill"
                  width={240}
                  height={134}
                  className="cm-builtbybill-mark h-16 w-auto object-contain"
                />
                <img
                  src={BUILT_BY_BILL_ICON_URL}
                  alt="Built By Bill icon"
                  width={96}
                  height={96}
                  className="cm-builtbybill-mark -ml-3 h-12 w-12 rounded-sm object-contain"
                />
              </a>
            </div>
            <div className="flex flex-col items-center gap-2 border-0 sm:hidden">
              <p
                style={{
                  color: "rgba(10,30,61,0.50)",
                  fontFamily: "Open Sans, sans-serif",
                  fontSize: "0.75rem",
                }}
              >
                © {new Date().getFullYear()}. All Rights Reserved.
              </p>
              <a
                href="https://builtbybill.design/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0 transition-opacity hover:opacity-80"
                aria-label="Built By Bill website"
              >
                <img
                  src={BUILT_BY_BILL_URL}
                  alt="Built By Bill"
                  width={240}
                  height={134}
                  className="cm-builtbybill-mark h-16 w-auto object-contain"
                />
                <img
                  src={BUILT_BY_BILL_ICON_URL}
                  alt="Built By Bill icon"
                  width={96}
                  height={96}
                  className="cm-builtbybill-mark -ml-3 h-12 w-12 rounded-sm object-contain"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Page ────────────────────────────────────────────────
export default function HomePage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen safe-shell" style={{ background: BG }}>
      <Seo
        title="Carson HVAC | Victoria BC Heating, Cooling & Air Conditioning"
        description="Carson HVAC provides reliable heating, cooling, ventilation, furnace repair, and air conditioning installation in Victoria, BC. Fast service, honest pricing, and expert technicians."
        canonicalPath="/"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "HVACBusiness",
            name: "Carson HVAC",
            areaServed: "Victoria, BC",
            url: "https://carsonhvacgroup.com/",
            telephone: "+12507449725",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Victoria",
              addressRegion: "BC",
              addressCountry: "CA",
            },
          },
        ]}
      />
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:rounded-md focus:bg-white focus:px-4 focus:py-3 focus:text-sm focus:font-semibold">
        Skip to content
      </a>
      <Navbar />
      <main id="main-content">
        <HeroSection />
        <div
          style={{
            marginTop: "calc(clamp(86px, 18vw, 120px) * -1)",
            paddingTop: "clamp(86px, 18vw, 120px)",
            backgroundImage: `url("${SECTION_BG}")`,
            backgroundSize: "cover",
            backgroundPosition: "center 8%",
            backgroundColor: BG,
          }}
        >
          <ServicesSection />
          <ServiceAreasSection />
          <BrandsSection />
          <AboutSection />
          <FAQSection />
          <div className="contact-footer-band">
            <ContactSection />
            <Footer />
          </div>
        </div>
      </main>
    </div>
  );
}
